import { useState } from "react";
import type { Workshop } from "@/content/workshops";
import {
  modalContentByWorkshop,
  type BringCategory,
  type BringContent,
  type BringItem,
} from "@/features/workshop/modals/modal-content";
import { resolveField } from "../workshop-override-content";
import {
  clearEffectiveOverrideField,
  setEffectiveOverride,
  useEffectiveWorkshopOverride,
} from "@/stores/useEffectiveWorkshopOverride";
import {
  AddButton,
  Field,
  IconBtn,
  ListCard,
  SectionFrame,
  Select,
  TextArea,
  TextInput,
} from "../editor-kit";
import { sameJson } from "../editor-utils";
import { Trash2 } from "lucide-react";

const EMPTY_ITEM: BringItem = { label: "", description: "" };
const EMPTY_CATEGORY: BringCategory = { label: "", tag: "REQUIRED", items: [] };

/** What-to-bring editor — categories of items + a closing note. */
export function BringEditor({ wsId }: { wsId: Workshop["id"] }) {
  const override = useEffectiveWorkshopOverride(wsId);
  const def = modalContentByWorkshop[wsId].bring;
  const current = resolveField(override, "bring", def);
  const [draft, setDraft] = useState<BringContent>(current);

  const setCategories = (categories: BringCategory[]) =>
    setDraft((d) => ({ ...d, categories }));
  const setCategory = (ci: number, patch: Partial<BringCategory>) =>
    setCategories(draft.categories.map((c, j) => (j === ci ? { ...c, ...patch } : c)));
  const setItem = (ci: number, ii: number, patch: Partial<BringItem>) =>
    setCategory(ci, {
      items: draft.categories[ci]!.items.map((it, j) => (j === ii ? { ...it, ...patch } : it)),
    });

  return (
    <SectionFrame
      description="The checklist shown in the What to Bring modal."
      overridden={override?.bring !== undefined}
      dirty={!sameJson(draft, current)}
      onSave={() => setEffectiveOverride(wsId, { bring: draft })}
      onReset={() => {
        clearEffectiveOverrideField(wsId, "bring");
        setDraft(def);
      }}
    >
      {draft.categories.map((c, ci) => (
        <ListCard
          key={ci}
          title={`Category ${ci + 1}`}
          onRemove={() => setCategories(draft.categories.filter((_, j) => j !== ci))}
        >
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <Field label="Category label">
              <TextInput value={c.label} onChange={(e) => setCategory(ci, { label: e.target.value })} />
            </Field>
            <Field label="Tag">
              <Select
                value={c.tag}
                onChange={(v) => setCategory(ci, { tag: v as BringCategory["tag"] })}
                options={[
                  { value: "REQUIRED", label: "REQUIRED" },
                  { value: "OPTIONAL", label: "OPTIONAL" },
                ]}
              />
            </Field>
          </div>
          {c.items.map((it, ii) => (
            <div
              key={ii}
              className="flex items-start gap-2 rounded border border-[rgba(var(--accent-true-blue-rgb),0.12)] p-2"
            >
              <div className="flex flex-1 flex-col gap-2">
                <TextInput
                  value={it.label}
                  placeholder="Item label"
                  onChange={(e) => setItem(ci, ii, { label: e.target.value })}
                />
                <TextInput
                  value={it.description}
                  placeholder="Item description"
                  onChange={(e) => setItem(ci, ii, { description: e.target.value })}
                />
              </div>
              <IconBtn
                label="Remove item"
                danger
                onClick={() =>
                  setCategory(ci, { items: c.items.filter((_, j) => j !== ii) })
                }
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden />
              </IconBtn>
            </div>
          ))}
          <AddButton
            label="Add item"
            onClick={() => setCategory(ci, { items: [...c.items, { ...EMPTY_ITEM }] })}
          />
        </ListCard>
      ))}
      <AddButton
        label="Add category"
        onClick={() => setCategories([...draft.categories, { ...EMPTY_CATEGORY, items: [] }])}
      />
      <Field label="Note">
        <TextArea value={draft.note} onChange={(e) => setDraft((d) => ({ ...d, note: e.target.value }))} />
      </Field>
    </SectionFrame>
  );
}
